import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/hooks/useAuth";
import { editUserDetailsSchema, type EditUserDetailsFormData } from "./edit-user-details-schema";
import { SETTINGS_FORM_STACK_CLASS } from "@/pages/settings/settings-layout-constants";
import { cn } from "@/lib/utils";

export function UserProfileForm() {
  const { user, onUpdateUserDetails } = useAuth();

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<EditUserDetailsFormData>({
    resolver: zodResolver(editUserDetailsSchema),
    defaultValues: {
      firstName: user?.firstName ?? "",
      lastName: user?.lastName ?? "",
    },
  });

  // `defaultValues` only seeds the form on first mount; reset when the redux user hydrates or changes.
  useEffect(() => {
    reset({
      firstName: user?.firstName ?? "",
      lastName: user?.lastName ?? "",
    });
  }, [user?.firstName, user?.lastName, reset]);

  const onSubmit = async (data: EditUserDetailsFormData) => {
    try {
      const result = await onUpdateUserDetails({
        ...data,
        email: user?.email ?? "",
        organizationId: user?.organizationId ?? "",
        lassoUserId: user?.lassoUserId ?? "",
        role: user?.role ?? "",
      });

      if (result.success) {
        toast.success("Profile updated.");
      } else {
        toast.error(result.error ?? "Failed to update profile");
      }
    } catch {
      toast.error("An unexpected error occurred");
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={cn("flex flex-col gap-4", SETTINGS_FORM_STACK_CLASS)}>
      <div className="space-y-2">
        <Label htmlFor="settings-profile-email">Email</Label>
        <Input
          id="settings-profile-email"
          type="email"
          value={user?.email ?? ""}
          disabled
          readOnly
          autoComplete="email"
        />
      </div>
      <div className="flex flex-row gap-2">
        <div className="flex-1 space-y-2">
          <Label htmlFor="settings-profile-firstName">First name</Label>
          <Controller
            control={control}
            name="firstName"
            render={({ field }) => (
              <Input id="settings-profile-firstName" type="text" {...field} placeholder="First name" autoComplete="given-name" />
            )}
          />
          {errors.firstName && <p className="text-destructive text-sm">{errors.firstName.message ?? "First name is required"}</p>}
        </div>

        <div className="flex-1 space-y-2">
          <Label htmlFor="settings-profile-lastName">Last name</Label>
          <Controller
            control={control}
            name="lastName"
            render={({ field }) => (
              <Input id="settings-profile-lastName" type="text" {...field} placeholder="Last name" autoComplete="family-name" />
            )}
          />
          {errors.lastName && <p className="text-destructive text-sm">{errors.lastName.message ?? "Last name is required"}</p>}
        </div>
      </div>

      <div className="flex flex-row justify-start gap-2 pt-2">
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </form>
  );
}
